if (!window.__tdEmblaComponentsInit) {
  window.__tdEmblaComponentsInit = true;

  function parseEmblaConfig(configRaw) {
    if (!configRaw) {
      return {};
    }

    try {
      return JSON.parse(configRaw);
    } catch (error) {
      console.warn('TD Embla - Invalid configuration JSON.', error);
      return {};
    }
  }

  function defineEmblaSliderElement() {
    if (customElements.get('embla-slider')) {
      return;
    }

    class EmblaSlider extends HTMLElement {
      static SELECTORS = {
        viewport: '[data-td-embla-viewport]',
      };

      constructor() {
        super();
        this.emblaApi = null;
        this.autoplayDelay = 0;
        this.autoplayTimer = null;
        this.autoplayEnabled = false;

        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleFocusIn = this.handleFocusIn.bind(this);
        this.handleFocusOut = this.handleFocusOut.bind(this);
        this.handleEmblaSelect = this.handleEmblaSelect.bind(this);
      }

      connectedCallback() {
        if (typeof window.EmblaCarousel !== 'function') {
          return;
        }

        const viewport = this.querySelector(EmblaSlider.SELECTORS.viewport);
        if (!(viewport instanceof HTMLElement)) {
          console.warn('TD Embla - Missing viewport element in embla-slider.');
          return;
        }

        const baseConfig = {
          loop: false,
          dragFree: true,
          align: 'start',
        };
        const rawConfig = this.dataset.tdEmblaConfig || this.getAttribute('embla-config');
        const parsedConfig = parseEmblaConfig(rawConfig);
        const mergedConfig = {
          ...baseConfig,
          ...parsedConfig,
        };

        const autoplayConfig = mergedConfig.autoplay;
        const autoplayDelayFromConfig = Number(mergedConfig.autoplayDelay);
        this.autoplayDelay = 0;

        if (
          autoplayConfig &&
          typeof autoplayConfig === 'object' &&
          !Array.isArray(autoplayConfig) &&
          Number(autoplayConfig.delay) > 0
        ) {
          this.autoplayDelay = Number(autoplayConfig.delay);
        } else if (autoplayDelayFromConfig > 0) {
          this.autoplayDelay = autoplayDelayFromConfig;
        }

        delete mergedConfig.autoplay;
        delete mergedConfig.autoplayDelay;

        this.emblaApi = window.EmblaCarousel(viewport, mergedConfig);
        this.setupAutoplay();
        this.dispatchEvent(
          new CustomEvent('embla:init', {
            bubbles: true,
            detail: {
              api: this.emblaApi,
            },
          })
        );
      }

      disconnectedCallback() {
        this.teardownAutoplay();

        if (this.emblaApi && typeof this.emblaApi.destroy === 'function') {
          this.emblaApi.destroy();
        }
        this.emblaApi = null;
      }

      setupAutoplay() {
        if (!this.emblaApi || this.autoplayDelay <= 0) {
          this.autoplayEnabled = false;
          return;
        }

        this.autoplayEnabled = true;
        this.emblaApi.on('pointerDown', this.handlePointerDown);
        this.emblaApi.on('pointerUp', this.handlePointerUp);
        this.emblaApi.on('select', this.handleEmblaSelect);
        this.emblaApi.on('reInit', this.handleEmblaSelect);

        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
        this.startAutoplay();
      }

      teardownAutoplay() {
        this.autoplayEnabled = false;
        this.clearAutoplayTimer();

        if (this.emblaApi) {
          this.emblaApi.off('pointerDown', this.handlePointerDown);
          this.emblaApi.off('pointerUp', this.handlePointerUp);
          this.emblaApi.off('select', this.handleEmblaSelect);
          this.emblaApi.off('reInit', this.handleEmblaSelect);
        }

        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
      }

      clearAutoplayTimer() {
        if (this.autoplayTimer) {
          window.clearTimeout(this.autoplayTimer);
          this.autoplayTimer = null;
        }
      }

      startAutoplay() {
        if (!this.autoplayEnabled || !this.emblaApi || this.autoplayDelay <= 0) {
          return;
        }

        this.clearAutoplayTimer();
        this.autoplayTimer = window.setTimeout(() => {
          this.autoplayTimer = null;

          if (!this.emblaApi) {
            return;
          }

          if (this.emblaApi.canScrollNext()) {
            this.emblaApi.scrollNext();
          } else {
            this.emblaApi.scrollTo(0);
          }
        }, this.autoplayDelay);
      }

      stopAutoplay() {
        this.clearAutoplayTimer();
      }

      handleEmblaSelect() {
        this.startAutoplay();
      }

      handlePointerDown() {
        this.stopAutoplay();
      }

      handlePointerUp() {
        this.startAutoplay();
      }

      handleMouseEnter() {
        this.stopAutoplay();
      }

      handleMouseLeave() {
        this.startAutoplay();
      }

      handleFocusIn() {
        this.stopAutoplay();
      }

      handleFocusOut(event) {
        const nextFocusedElement = event.relatedTarget;
        if (nextFocusedElement instanceof Node && this.contains(nextFocusedElement)) {
          return;
        }

        this.startAutoplay();
      }
    }

    customElements.define('embla-slider', EmblaSlider);
  }

  function defineEmblaDotsElement() {
    if (customElements.get('embla-dots')) {
      return;
    }

    class EmblaDots extends HTMLElement {
      static SELECTORS = {
        template: 'template',
        dotsList: '[data-td-embla-dots-list]',
        dotButton: '[data-td-embla-dot]',
      };

      constructor() {
        super();
        this.emblaApi = null;
        this.targetEmblaSlider = null;
        this.dotListeners = [];

        this.handleEmblaInit = this.handleEmblaInit.bind(this);
        this.handleSelect = this.handleSelect.bind(this);
        this.handleReInit = this.handleReInit.bind(this);
      }

      connectedCallback() {
        const emblaId = this.getAttribute('embla-id');
        if (!emblaId) {
          console.warn('TD Embla - embla-dots requires an embla-id attribute.');
          return;
        }

        this.targetEmblaSlider = document.getElementById(emblaId);
        if (!(this.targetEmblaSlider instanceof HTMLElement)) {
          console.warn(`TD Embla - embla-dots target not found: ${emblaId}`);
          return;
        }

        if (this.targetEmblaSlider.emblaApi) {
          this.attachEmblaApi(this.targetEmblaSlider.emblaApi);
          return;
        }

        this.targetEmblaSlider.addEventListener('embla:init', this.handleEmblaInit);
      }

      disconnectedCallback() {
        this.detachEmblaApi();

        if (this.targetEmblaSlider) {
          this.targetEmblaSlider.removeEventListener('embla:init', this.handleEmblaInit);
        }

        this.targetEmblaSlider = null;
      }

      handleEmblaInit(event) {
        const emblaApi = event?.detail?.api || this.targetEmblaSlider?.emblaApi;
        if (emblaApi) {
          this.attachEmblaApi(emblaApi);
        }
      }

      attachEmblaApi(emblaApi) {
        this.detachEmblaApi();

        this.emblaApi = emblaApi;
        this.renderDots();

        this.emblaApi.on('select', this.handleSelect);
        this.emblaApi.on('reInit', this.handleReInit);
        this.handleSelect();
      }

      detachEmblaApi() {
        if (this.emblaApi) {
          this.emblaApi.off('select', this.handleSelect);
          this.emblaApi.off('reInit', this.handleReInit);
        }

        this.removeDotListeners();

        const dotsList = this.querySelector(EmblaDots.SELECTORS.dotsList);
        if (dotsList instanceof HTMLElement) {
          dotsList.innerHTML = '';
        }

        this.emblaApi = null;
      }

      handleReInit() {
        this.renderDots();
        this.handleSelect();
      }

      renderDots() {
        if (!this.emblaApi) {
          return;
        }

        const dotsTemplate = this.querySelector(EmblaDots.SELECTORS.template);
        const dotsList = this.querySelector(EmblaDots.SELECTORS.dotsList);
        if (!(dotsTemplate instanceof HTMLTemplateElement) || !(dotsList instanceof HTMLElement)) {
          return;
        }

        const dotTemplate = dotsTemplate.innerHTML;
        const snapPoints = this.emblaApi.scrollSnapList();
        const slidesCount = snapPoints.length;

        dotsList.innerHTML = snapPoints
          .map((_, index) => {
            return dotTemplate
              .replace(/__INDEX__/g, String(index + 1))
              .replace(/__LENGTH__/g, String(slidesCount));
          })
          .join('');

        this.removeDotListeners();
        const dotNodes = dotsList.querySelectorAll(EmblaDots.SELECTORS.dotButton);
        dotNodes.forEach((dotNode, index) => {
          const listener = () => this.scrollTo(index);
          dotNode.addEventListener('click', listener, false);
          this.dotListeners.push({
            dotNode,
            listener,
          });
        });
      }

      removeDotListeners() {
        this.dotListeners.forEach(({ dotNode, listener }) => {
          dotNode.removeEventListener('click', listener, false);
        });
        this.dotListeners = [];
      }

      handleSelect() {
        if (!this.emblaApi) {
          return;
        }

        const selectedIndex = this.emblaApi.selectedScrollSnap();
        const dotNodes = this.querySelectorAll(EmblaDots.SELECTORS.dotButton);

        dotNodes.forEach((dotNode, index) => {
          const isSelected = index === selectedIndex;
          dotNode.classList.toggle('td-embla-dots__dot--selected', isSelected);
          dotNode.classList.toggle('selected', isSelected);
          dotNode.setAttribute('aria-current', isSelected ? 'true' : 'false');
        });
      }

      scrollTo(index) {
        if (!this.emblaApi) {
          return;
        }

        this.emblaApi.scrollTo(index);
      }
    }

    customElements.define('embla-dots', EmblaDots);
  }

  function defineEmblaControlsElement() {
    if (customElements.get('embla-controls')) {
      return;
    }

    class EmblaControls extends HTMLElement {
      static SELECTORS = {
        previousButton: '[data-td-action="prev"]',
        nextButton: '[data-td-action="next"]',
      };

      constructor() {
        super();
        this.emblaApi = null;
        this.targetEmblaSlider = null;
        this.previousButton = null;
        this.nextButton = null;
        this.hideAtBounds = false;

        this.handleEmblaInit = this.handleEmblaInit.bind(this);
        this.handleSelect = this.handleSelect.bind(this);
        this.handlePreviousClick = this.handlePreviousClick.bind(this);
        this.handleNextClick = this.handleNextClick.bind(this);
      }

      connectedCallback() {
        const emblaId = this.getAttribute('embla-id');
        if (!emblaId) {
          console.warn('TD Embla - embla-controls requires an embla-id attribute.');
          return;
        }

        this.targetEmblaSlider = document.getElementById(emblaId);
        if (!(this.targetEmblaSlider instanceof HTMLElement)) {
          console.warn(`TD Embla - embla-controls target not found: ${emblaId}`);
          return;
        }

        this.previousButton = this.querySelector(EmblaControls.SELECTORS.previousButton);
        this.nextButton = this.querySelector(EmblaControls.SELECTORS.nextButton);
        this.hideAtBounds = this.hasAttribute('hide-at-bounds');

        if (!this.previousButton && !this.nextButton) {
          console.warn('TD Embla - embla-controls requires at least one control button.');
          return;
        }

        if (this.targetEmblaSlider.emblaApi) {
          this.attachEmblaApi(this.targetEmblaSlider.emblaApi);
          return;
        }

        this.targetEmblaSlider.addEventListener('embla:init', this.handleEmblaInit);
      }

      disconnectedCallback() {
        this.detachEmblaApi();

        if (this.targetEmblaSlider) {
          this.targetEmblaSlider.removeEventListener('embla:init', this.handleEmblaInit);
        }

        this.targetEmblaSlider = null;
        this.previousButton = null;
        this.nextButton = null;
      }

      handleEmblaInit(event) {
        const emblaApi = event?.detail?.api || this.targetEmblaSlider?.emblaApi;
        if (emblaApi) {
          this.attachEmblaApi(emblaApi);
        }
      }

      attachEmblaApi(emblaApi) {
        this.detachEmblaApi();

        this.emblaApi = emblaApi;

        if (this.previousButton) {
          this.previousButton.addEventListener('click', this.handlePreviousClick);
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('click', this.handleNextClick);
        }

        this.emblaApi.on('select', this.handleSelect);
        this.emblaApi.on('reInit', this.handleSelect);

        this.handleSelect();
      }

      detachEmblaApi() {
        if (this.previousButton) {
          this.previousButton.removeEventListener('click', this.handlePreviousClick);
        }

        if (this.nextButton) {
          this.nextButton.removeEventListener('click', this.handleNextClick);
        }

        if (this.emblaApi) {
          this.emblaApi.off('select', this.handleSelect);
          this.emblaApi.off('reInit', this.handleSelect);
        }

        this.emblaApi = null;
      }

      handlePreviousClick() {
        if (!this.emblaApi) {
          return;
        }

        this.emblaApi.scrollPrev();
      }

      handleNextClick() {
        if (!this.emblaApi) {
          return;
        }

        this.emblaApi.scrollNext();
      }

      handleSelect() {
        if (!this.emblaApi) {
          return;
        }

        this.updateButtonState(this.previousButton, this.emblaApi.canScrollPrev());
        this.updateButtonState(this.nextButton, this.emblaApi.canScrollNext());
      }

      updateButtonState(buttonElement, canScroll) {
        if (!(buttonElement instanceof HTMLElement)) {
          return;
        }

        buttonElement.toggleAttribute('disabled', !canScroll);

        if (this.hideAtBounds) {
          buttonElement.classList.toggle('td-embla-controls__btn--hidden', !canScroll);
        } else {
          buttonElement.classList.remove('td-embla-controls__btn--hidden');
        }
      }
    }

    customElements.define('embla-controls', EmblaControls);
  }

  function declareEmblaCustomElements() {
    defineEmblaSliderElement();
    defineEmblaDotsElement();
    defineEmblaControlsElement();
  }

  window.__tdDefineEmblaCustomElements = declareEmblaCustomElements;

  if (typeof window.EmblaCarousel === 'function') {
    declareEmblaCustomElements();
  }
}

if (!window.__tdEmblaLoaderInit) {
  window.__tdEmblaLoaderInit = true;

  const EMBLA_LOADER_SELECTOR = '[data-td-embla-loader]';
  const EMBLA_COMPONENTS_SELECTOR = 'embla-slider, embla-controls, embla-dots';
  let emblaAssetsPromise = null;

  function loadScriptOnce(url, globalName) {
    if (!url) {
      return Promise.resolve();
    }

    if (globalName && window[globalName]) {
      return Promise.resolve();
    }

    const existingScript = document.querySelector(`script[src="${url}"]`);
    if (existingScript) {
      if (globalName && window[globalName]) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        existingScript.addEventListener(
          'load',
          () => {
            resolve();
          },
          { once: true }
        );

        existingScript.addEventListener(
          'error',
          () => {
            reject(new Error(`TD Embla - Failed to load script: ${url}`));
          },
          { once: true }
        );
      });
    }

    return new Promise((resolve, reject) => {
      const scriptElement = document.createElement('script');
      scriptElement.src = url;
      scriptElement.async = true;
      scriptElement.onload = () => resolve();
      scriptElement.onerror = () => reject(new Error(`TD Embla - Failed to load script: ${url}`));
      document.head.appendChild(scriptElement);
    });
  }

  function loadEmblaAssets() {
    if (emblaAssetsPromise) {
      return emblaAssetsPromise;
    }

    const loaderElement = document.querySelector(EMBLA_LOADER_SELECTOR);
    if (!loaderElement) {
      return Promise.resolve();
    }

    const emblaScriptURL = loaderElement.dataset.tdEmblaScript;
    emblaAssetsPromise = loadScriptOnce(emblaScriptURL, 'EmblaCarousel')
      .then(() => {
        if (typeof window.__tdDefineEmblaCustomElements === 'function') {
          window.__tdDefineEmblaCustomElements();
        }
      })
      .catch((error) => {
        console.warn('TD Embla - Failed to initialize Embla assets.', error);
      });

    return emblaAssetsPromise;
  }

  function initializeWhenNeeded() {
    if (!document.querySelector(EMBLA_COMPONENTS_SELECTOR)) {
      return;
    }

    loadEmblaAssets();
  }

  if (Shopify.designMode) {
    loadEmblaAssets();
    document.addEventListener('shopify:section:load', initializeWhenNeeded);
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWhenNeeded);
  } else {
    initializeWhenNeeded();
  }
}
